import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Dropdown from "../../../Generic/Dropdown";
import { OFFER_CHANNEL_OPTIONS, OFFER_TYPE_OPTIONS, OfferField, PRIORITY_DROPDOWN_OPTIONS, SUB_OFFER_TYPE_OPTIONS } from "../../constants";
import { Section, SectionTitle, FieldRow, Field, FullWidthField, FieldLabel, FieldInput, FieldTextarea, DropdownWrap, ErrorText } from "../shared/OfferFormLayout";
import { buildOfferGroupDropdownOptions } from "../../utils/offerValidation";

const BasicInformationStep = ({ draft, errors, offerGroups, onChange }) => {
	const groupOptions = useMemo(() => buildOfferGroupDropdownOptions(offerGroups), [offerGroups]);

	function update(field, value) {
		onChange({ [field]: value }, [field]);
	}

	return (
		<Section>
			<SectionTitle>Basic Information</SectionTitle>
			<FieldRow>
				<Field id="offer-basic-title-field">
					<FieldLabel htmlFor="offer-basic-title">Title *</FieldLabel>
					<FieldInput
						id="offer-basic-title"
						value={draft[OfferField.TITLE]}
						onChange={(e) => update(OfferField.TITLE, e.target.value)}
						errorMessage={errors[OfferField.TITLE]}
						errored={typeof errors[OfferField.TITLE] !== "undefined"}
					/>
				</Field>
				<Field id="offer-basic-offer-code-field">
					<FieldLabel htmlFor="offer-basic-offer-code">Offer Code *</FieldLabel>
					<FieldInput
						id="offer-basic-offer-code"
						value={draft[OfferField.OFFER_CODE]}
						onChange={(e) => update(OfferField.OFFER_CODE, e.target.value)}
						errorMessage={errors[OfferField.OFFER_CODE]}
						errored={typeof errors[OfferField.OFFER_CODE] !== "undefined"}
					/>
				</Field>
			</FieldRow>

			<FullWidthField id="offer-basic-description-field">
				<FieldLabel htmlFor="offer-basic-description">Description</FieldLabel>
				<FieldTextarea
					id="offer-basic-description"
					value={draft[OfferField.DESCRIPTION]}
					onChange={(e) => onChange({ [OfferField.DESCRIPTION]: e.target.value })}
					height="5rem"
				/>
			</FullWidthField>

			<FieldRow>
				<Field id="offer-basic-offer-type-field">
					<FieldLabel htmlFor="offer-basic-offer-type">Offer Type *</FieldLabel>
					<DropdownWrap id="offer-basic-offer-type">
						<Dropdown
							head="Select type"
							optionsList={OFFER_TYPE_OPTIONS}
							value={draft[OfferField.OFFER_TYPE]}
							onChange={(v) => update(OfferField.OFFER_TYPE, v)}
						/>
					</DropdownWrap>
					{errors[OfferField.OFFER_TYPE] && (
						<ErrorText>{errors[OfferField.OFFER_TYPE]}</ErrorText>
					)}
				</Field>
				<Field id="offer-basic-sub-offer-type-field">
					<FieldLabel htmlFor="offer-basic-sub-offer-type">Sub Offer Type *</FieldLabel>
					<DropdownWrap id="offer-basic-sub-offer-type">
						<Dropdown
							head="Select sub type"
							optionsList={SUB_OFFER_TYPE_OPTIONS}
							value={draft[OfferField.SUB_OFFER_TYPE]}
							onChange={(v) => update(OfferField.SUB_OFFER_TYPE, v)}
						/>
					</DropdownWrap>
					{errors[OfferField.SUB_OFFER_TYPE] && (
						<ErrorText>{errors[OfferField.SUB_OFFER_TYPE]}</ErrorText>
					)}
				</Field>
			</FieldRow>

			<FieldRow>
				<Field id="offer-basic-offer-group-field">
					<FieldLabel htmlFor="offer-basic-offer-group">Offer Group *</FieldLabel>
					<DropdownWrap id="offer-basic-offer-group">
						<Dropdown
							head="Select group"
							optionsList={groupOptions}
							value={draft[OfferField.OFFER_GROUP_ID]}
							onChange={(v) => update(OfferField.OFFER_GROUP_ID, v)}
						/>
					</DropdownWrap>
					{errors[OfferField.OFFER_GROUP_ID] && (
						<ErrorText>{errors[OfferField.OFFER_GROUP_ID]}</ErrorText>
					)}
				</Field>
				<Field id="offer-basic-priority-field">
					<FieldLabel htmlFor="offer-basic-priority">Priority *</FieldLabel>
					<DropdownWrap id="offer-basic-priority">
						<Dropdown
							head="Select priority"
							optionsList={PRIORITY_DROPDOWN_OPTIONS}
							value={draft[OfferField.PRIORITY]}
							onChange={(v) => update(OfferField.PRIORITY, v)}
						/>
					</DropdownWrap>
					{errors[OfferField.PRIORITY] && (
						<ErrorText>{errors[OfferField.PRIORITY]}</ErrorText>
					)}
				</Field>
				<Field id="offer-basic-channel-field">
					<FieldLabel htmlFor="offer-basic-channel">Channel *</FieldLabel>
					<DropdownWrap id="offer-basic-channel">
						<Dropdown
							head="Select channel"
							optionsList={OFFER_CHANNEL_OPTIONS}
							value={draft[OfferField.CHANNEL]}
							onChange={(v) => update(OfferField.CHANNEL, v)}
						/>
					</DropdownWrap>
					{errors[OfferField.CHANNEL] && (
						<ErrorText>{errors[OfferField.CHANNEL]}</ErrorText>
					)}
				</Field>
			</FieldRow>

			<SectionTitle>Validity Period</SectionTitle>
			<FieldRow>
				<Field id="offer-basic-start-date-field">
					<FieldLabel htmlFor="offer-basic-start-date">Start Date *</FieldLabel>
					<DatePicker
						id="offer-basic-start-date"
						selected={
							draft[OfferField.START_DATE] ? new Date(draft[OfferField.START_DATE]) : null
						}
						onChange={(date) => update(OfferField.START_DATE, date)}
						dateFormat="dd/MM/yyyy"
						placeholderText="Select start date"
					/>
					{errors[OfferField.START_DATE] && (
						<ErrorText>{errors[OfferField.START_DATE]}</ErrorText>
					)}
				</Field>
				<Field id="offer-basic-end-date-field">
					<FieldLabel htmlFor="offer-basic-end-date">End Date *</FieldLabel>
					<DatePicker
						id="offer-basic-end-date"
						selected={
							draft[OfferField.END_DATE] ? new Date(draft[OfferField.END_DATE]) : null
						}
						onChange={(date) => update(OfferField.END_DATE, date)}
						dateFormat="dd/MM/yyyy"
						minDate={
							draft[OfferField.START_DATE]
								? new Date(draft[OfferField.START_DATE])
								: null
						}
						placeholderText="Select end date"
					/>
					{errors[OfferField.END_DATE] && (
						<ErrorText>{errors[OfferField.END_DATE]}</ErrorText>
					)}
				</Field>
			</FieldRow>

			<SectionTitle>Usage Limits</SectionTitle>
			<FieldRow>
				<Field id="offer-basic-max-usage-field">
					<FieldLabel htmlFor="offer-basic-max-usage">Max Usage Count *</FieldLabel>
					<FieldInput
						id="offer-basic-max-usage"
						type="number"
						min="0"
						value={draft[OfferField.MAX_USAGE_COUNT]}
						onChange={(e) => update(OfferField.MAX_USAGE_COUNT, e.target.value)}
						errorMessage={errors[OfferField.MAX_USAGE_COUNT]}
						errored={typeof errors[OfferField.MAX_USAGE_COUNT] !== "undefined"}
					/>
				</Field>
				<Field id="offer-basic-individual-max-usage-field">
					<FieldLabel htmlFor="offer-basic-individual-max-usage">
						Individual Max Usage *
					</FieldLabel>
					<FieldInput
						id="offer-basic-individual-max-usage"
						type="number"
						min="0"
						value={draft[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]}
						onChange={(e) =>
							update(OfferField.INDIVIDUAL_MAX_USAGE_COUNT, e.target.value)
						}
						errorMessage={errors[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]}
						errored={
							typeof errors[OfferField.INDIVIDUAL_MAX_USAGE_COUNT] !== "undefined"
						}
					/>
				</Field>
			</FieldRow>
		</Section>
	);
};

export default BasicInformationStep;
